/* ============================================================
   PADION Theme JS
   - Scroll-reveal (Intersection Observer)
   - Sticky header
   - Mobile menu
   - Cart drawer (Shopify AJAX API)
   - Product gallery
   - Variant selector
   - Accordion
   ============================================================ */

(function () {
  'use strict';

  /* ── Scroll Reveal ──────────────────────────────────────── */
  function initReveal() {
    const els = document.querySelectorAll('.reveal, .reveal-scale');
    if (!els.length) return;

    // Fallback: show all after 800ms in case IntersectionObserver is slow
    const fallback = setTimeout(() => {
      els.forEach((el) => el.classList.add('is-visible'));
    }, 800);

    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('is-visible');
          obs.unobserve(e.target);
        }
      }),
      { rootMargin: '50px' }
    );
    els.forEach((el) => obs.observe(el));

    // Clear fallback if observer fires quickly
    setTimeout(() => clearTimeout(fallback), 100);
  }

  /* ── Sticky Header ──────────────────────────────────────── */
  function initHeader() {
    const header = document.getElementById('padion-header');
    if (!header) return;
    const onScroll = () => header.classList.toggle('scrolled', window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ── Mobile Menu ────────────────────────────────────────── */
  function initMobileMenu() {
    const toggle = document.getElementById('menu-toggle');
    const close  = document.getElementById('menu-close');
    const menu   = document.getElementById('mobile-menu');
    if (!toggle || !menu) return;
    toggle.addEventListener('click', () => { menu.classList.add('open'); document.body.style.overflow = 'hidden'; });
    if (close) close.addEventListener('click', closeMenu);
    menu.addEventListener('click', (e) => { if (e.target === menu) closeMenu(); });
    function closeMenu() { menu.classList.remove('open'); document.body.style.overflow = ''; }
  }

  /* ── Cart State ─────────────────────────────────────────── */
  let cartDrawerOpen = false;

  function formatMoney(cents) {
    const num = cents / 100;
    return 'CHF ' + (num % 1 === 0 ? num.toFixed(0) : num.toFixed(2)) + '.–';
  }

  async function fetchCart() {
    const res = await fetch('/cart.js');
    return res.json();
  }

  function updateCartCount(count) {
    const badge = document.getElementById('cart-count');
    if (!badge) return;
    badge.textContent = count;
    badge.classList.toggle('hidden', count === 0);
  }

  function renderCartItems(cart) {
    const itemsEl  = document.getElementById('cart-items');
    const emptyEl  = document.getElementById('cart-empty');
    const footerEl = document.getElementById('cart-footer');
    const subtotalEl = document.getElementById('cart-subtotal-value');
    const checkoutBtn = document.getElementById('cart-checkout-btn');

    if (!itemsEl) return;

    if (!cart.items || cart.items.length === 0) {
      itemsEl.innerHTML = '';
      if (emptyEl)  emptyEl.classList.remove('hidden');
      if (footerEl) footerEl.classList.add('hidden');
      return;
    }

    if (emptyEl)  emptyEl.classList.add('hidden');
    if (footerEl) footerEl.classList.remove('hidden');
    if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);
    if (checkoutBtn) checkoutBtn.href = '/checkout';

    itemsEl.innerHTML = cart.items.map((item) => `
      <div class="cart-item">
        <div class="cart-item-img">
          ${item.image ? `<img src="${item.image}" alt="${item.product_title}" loading="lazy">` : ''}
        </div>
        <div class="cart-item-info">
          <p class="cart-item-title">${item.product_title}</p>
          <p class="cart-item-variant">${item.variant_title !== 'Default Title' ? item.variant_title : ''}</p>
          <div class="cart-item-row">
            <span class="cart-item-price">${formatMoney(item.final_line_price)}</span>
            <button class="cart-item-remove" data-key="${item.key}">Remove</button>
          </div>
        </div>
      </div>
    `).join('');

    itemsEl.querySelectorAll('.cart-item-remove').forEach((btn) => {
      btn.addEventListener('click', async () => {
        await updateCartItem(btn.dataset.key, 0);
      });
    });
  }

  async function updateCartItem(key, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    });
    const cart = await res.json();
    updateCartCount(cart.item_count);
    renderCartItems(cart);
  }

  /* ── Cart Drawer ────────────────────────────────────────── */
  function initCartDrawer() {
    const overlay  = document.getElementById('cart-overlay');
    const drawer   = document.getElementById('cart-drawer');
    const closeBtn = document.getElementById('cart-drawer-close');
    const cartBtns = document.querySelectorAll('[data-open-cart]');

    if (!drawer) return;

    function openCart() {
      cartDrawerOpen = true;
      overlay && overlay.classList.add('open');
      drawer.classList.add('open');
      document.body.style.overflow = 'hidden';
      fetchCart().then((cart) => {
        updateCartCount(cart.item_count);
        renderCartItems(cart);
      });
    }

    function closeCart() {
      cartDrawerOpen = false;
      overlay && overlay.classList.remove('open');
      drawer.classList.remove('open');
      document.body.style.overflow = '';
    }

    cartBtns.forEach((btn) => btn.addEventListener('click', openCart));
    if (closeBtn) closeBtn.addEventListener('click', closeCart);
    if (overlay)  overlay.addEventListener('click', closeCart);

    fetchCart().then((cart) => updateCartCount(cart.item_count));
  }

  /* ── Add to Cart ────────────────────────────────────────── */
  function initAddToCart() {
    const form = document.getElementById('product-form');
    if (!form) return;

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const btn = form.querySelector('.add-to-cart-btn');
      const variantId = form.querySelector('[name="id"]')?.value;
      if (!variantId) return;

      btn.disabled = true;
      btn.textContent = '...';

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 }),
        });
        if (!res.ok) throw new Error();
        const cart = await fetchCart();
        updateCartCount(cart.item_count);
        renderCartItems(cart);

        const overlay = document.getElementById('cart-overlay');
        const drawer  = document.getElementById('cart-drawer');
        if (overlay) overlay.classList.add('open');
        if (drawer)  drawer.classList.add('open');
        document.body.style.overflow = 'hidden';

        btn.textContent = 'ADDED';
        setTimeout(() => {
          btn.disabled = false;
          btn.textContent = 'ADD TO CART';
        }, 1500);
      } catch {
        btn.disabled = false;
        btn.textContent = 'ADD TO CART';
      }
    });
  }

  /* ── Product Gallery ────────────────────────────────────── */
  function initGallery() {
    const mainImg = document.getElementById('product-main-img');
    const thumbs  = document.querySelectorAll('.product-thumb');
    if (!mainImg || !thumbs.length) return;

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const src = thumb.dataset.src;
        const alt = thumb.dataset.alt;
        if (src) { mainImg.src = src; if (alt) mainImg.alt = alt; }
        thumbs.forEach((t) => t.classList.remove('active'));
        thumb.classList.add('active');
      });
    });
    if (thumbs[0]) thumbs[0].classList.add('active');
  }

  /* ── Variant Selector ───────────────────────────────────── */
  function initVariantSelector() {
    const btns     = document.querySelectorAll('.option-btn');
    const idInput  = document.getElementById('variant-id');
    const priceEl  = document.getElementById('product-price');
    const cartBtn  = document.querySelector('.add-to-cart-btn');

    if (!btns.length || !idInput) return;

    const selections = {};

    btns.forEach((btn) => {
      btn.addEventListener('click', () => {
        const name = btn.dataset.optionName;
        const val  = btn.dataset.optionValue;
        selections[name] = val;

        document.querySelectorAll(`.option-btn[data-option-name="${name}"]`).forEach((b) => b.classList.remove('selected'));
        btn.classList.add('selected');

        const variantsData = window.__padionVariants;
        if (!variantsData) return;

        const match = variantsData.find((v) =>
          v.options.every((o, i) => selections[`option${i + 1}`] === o || Object.values(selections).includes(o))
        );
        if (match) {
          idInput.value = match.id;
          if (priceEl) priceEl.textContent = formatMoney(match.price);
          if (cartBtn) {
            cartBtn.disabled = !match.available;
            cartBtn.textContent = match.available ? 'ADD TO CART' : 'SOLD OUT';
          }
        }
      });
    });
  }

  /* ── Accordion ──────────────────────────────────────────── */
  function initAccordions() {
    document.querySelectorAll('.accordion-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const content = btn.nextElementSibling;
        if (!content) return;
        const isOpen = content.classList.contains('open');
        content.classList.toggle('open', !isOpen);
        btn.classList.toggle('open', !isOpen);
      });
    });
  }

  /* ── Notify Form ────────────────────────────────────────── */
  function initNotifyForm() {
    const form    = document.getElementById('notify-form');
    const success = document.getElementById('notify-success');
    const errorEl = document.getElementById('notify-error');
    if (!form) return;

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const emailInput = form.querySelector('input[type="email"]');
      const honeypot   = form.querySelector('input[name="website"]');
      const btn        = form.querySelector('button[type="submit"]');

      if (honeypot && honeypot.value) return;
      if (!emailInput || !emailInput.value) return;

      btn.disabled = true;
      if (errorEl) errorEl.classList.add('hidden');

      try {
        const res = await fetch('/contact', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({
            form_type: 'contact',
            utf8: '✓',
            'contact[email]': emailInput.value,
            'contact[body]': 'Waitlist signup from padion.shop',
          }),
        });
        if (res.ok || res.redirected) {
          form.classList.add('hidden');
          if (success) success.classList.remove('hidden');
        } else {
          throw new Error();
        }
      } catch {
        if (errorEl) errorEl.classList.remove('hidden');
        btn.disabled = false;
      }
    });
  }

  /* ── Active Nav ─────────────────────────────────────────── */
  function initActiveNav() {
    const path = window.location.pathname;
    document.querySelectorAll('.header-nav a, .mobile-nav a').forEach((link) => {
      if (path.includes(link.getAttribute('href')?.split('/').pop() || '___')) {
        link.classList.add('active');
      }
    });
  }

  /* ── Init ───────────────────────────────────────────────── */
  document.addEventListener('DOMContentLoaded', () => {
    initHeader();
    initReveal();
    initMobileMenu();
    initCartDrawer();
    initAddToCart();
    initGallery();
    initVariantSelector();
    initAccordions();
    initNotifyForm();
    initActiveNav();
  });
})();
